import { createFileRoute, Link } from "@tanstack/react-router";
import { LayoutGrid, List, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";

import { CarGrid } from "@/components/site/CarGrid";
import { FilterSidebar, defaultFilters, type Filters } from "@/components/site/FilterSidebar";
import { SearchWidget } from "@/components/site/SearchWidget";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cars, MAIN_LOCATION } from "@/data/cars";
import { publicPageHeaders } from "@/lib/http-cache";

const title = "Rent a Car Bucharest — 28 Cars, No Deposit, €44/Day";
const description =
  "Compare all 28 rental cars in Bucharest from €44/day. Zero deposit, no credit card, free Otopeni pickup. Filter by brand, fuel and price, book in 2 min.";

type Sort = "popularity" | "price-asc" | "price-desc";

const rentalFaqs = [
  {
    q: "Is a deposit or credit card required?",
    a: "No. We never block a security deposit and we do not ask for a credit card. You can pay by debit card, in cash or by bank transfer when you collect the car.",
  },
  {
    q: "What is included in the daily price?",
    a: "Full insurance (CASCO and RCA), the Romanian rovinieta, unlimited mileage inside Romania, roadside assistance and free delivery in Bucharest or at Otopeni Airport.",
  },
  {
    q: "What documents do I need?",
    a: "A driving licence held for at least one year, a passport or ID card, and a minimum age of 21. Licences issued outside the EU should be accompanied by an international driving permit.",
  },
  {
    q: "Can I pick the car up at Otopeni Airport?",
    a: "Yes. Airport delivery and return are free at any hour; we meet you in the arrivals area with the keys and the contract. A custom address anywhere else costs 20 EUR.",
  },
  {
    q: "How long can I rent for?",
    a: "From one day to several months. Rentals of 7 days or more get a lower daily rate, and monthly rates are available on request by phone or email.",
  },
  {
    q: "Can I drive the car outside Romania?",
    a: "Cross-border travel within the EU is possible with prior written approval, so tell us your itinerary when you book and we prepare the green card documents.",
  },
];


export const Route = createFileRoute("/rentals/")({
  headers: publicPageHeaders,
  validateSearch: (
    search: Record<string, unknown>,
  ): { location?: string; pickup?: string; return?: string } => ({
    ...(typeof search["location"] === "string" ? { location: search["location"] } : {}),
    ...(typeof search["pickup"] === "string" ? { pickup: search["pickup"] } : {}),
    ...(typeof search["return"] === "string" ? { return: search["return"] } : {}),
  }),
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: "https://carentals.ro/rentals" },
    ],
    links: [{ rel: "canonical", href: "https://carentals.ro/rentals" }],
  }),

  component: RentalsPage,
});

function RentalsPage() {
  const search = Route.useSearch();
  const [filters, setFilters] = useState<Filters>({
    ...defaultFilters,
    location: search.location === MAIN_LOCATION ? MAIN_LOCATION : "Anywhere",
  });
  const [sort, setSort] = useState<Sort>("popularity");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [perPage, setPerPage] = useState(8);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = cars.filter((car) => {
      if (filters.location !== "Anywhere" && car.location !== filters.location) return false;
      if (filters.brands.length && !filters.brands.includes(car.brand)) return false;
      if (filters.bodyTypes.length && !filters.bodyTypes.includes(car.bodyType)) return false;
      if (filters.fuelTypes.length && !filters.fuelTypes.includes(car.fuelType)) return false;
      if (filters.transmissions.length && !filters.transmissions.includes(car.transmission))
        return false;
      if (car.seats < filters.seats[0] || car.seats > filters.seats[1]) return false;
      if (car.year < filters.years[0] || car.year > filters.years[1]) return false;
      if (car.pricePerDay < filters.price[0] || car.pricePerDay > filters.price[1]) return false;
      if (car.rating < filters.rating) return false;
      return true;
    });

    return [...list].sort((a, b) => {
      if (sort === "price-asc") return a.pricePerDay - b.pricePerDay;
      if (sort === "price-desc") return b.pricePerDay - a.pricePerDay;
      return b.popularity - a.popularity;
    });
  }, [filters, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const updateFilters = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  const sidebar = <FilterSidebar filters={filters} onChange={updateFilters} />;

  return (
    <div className="bg-secondary/40">
      <section className="border-b border-border bg-card">
        <div className="container-page py-10 lg:py-14">
          <p className="eyebrow">Rental grid</p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Rent a car in Bucharest</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            {cars.length} premium and economy cars available now, with free delivery inside
            Bucharest.
          </p>
          <SearchWidget
            className="mt-8"
            defaultLocation={search.location ?? "All Location"}
          />
        </div>
      </section>

      <div className="container-page grid gap-8 py-10 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]">
        <div className="hidden lg:block">{sidebar}</div>

        <div className="min-w-0">
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-2xl border border-border bg-card p-4 shadow-card sm:flex sm:justify-between">
            <p className="min-w-0 truncate text-sm text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{visible.length}</span> of{" "}
              {filtered.length} cars
            </p>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden">
                    <SlidersHorizontal className="size-4" /> Filters
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[20rem] overflow-y-auto p-4">
                  <SheetTitle className="sr-only">Filters</SheetTitle>
                  {sidebar}
                </SheetContent>
              </Sheet>

              <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
                <SelectTrigger aria-label="Sort cars" className="h-8 w-[10.5rem] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popularity">Popularity</SelectItem>
                  <SelectItem value="price-asc">Price Low to High</SelectItem>
                  <SelectItem value="price-desc">Price High to Low</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={String(perPage)}
                onValueChange={(v) => {
                  setPerPage(Number(v));
                  setPage(1);
                }}
              >
                <SelectTrigger aria-label="Cars per page" className="h-8 w-[5.5rem] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[8, 10, 12].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n} / page
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex overflow-hidden rounded-md border border-border">
                <Button
                  variant={view === "grid" ? "gold" : "ghost"}
                  size="icon"
                  className="size-8 rounded-none"
                  aria-label="Grid view"
                  onClick={() => setView("grid")}
                >
                  <LayoutGrid className="size-4" />
                </Button>
                <Button
                  variant={view === "list" ? "gold" : "ghost"}
                  size="icon"
                  className="size-8 rounded-none"
                  aria-label="List view"
                  onClick={() => setView("list")}
                >
                  <List className="size-4" />
                </Button>
              </div>
            </div>
          </div>

          <CarGrid cars={visible} view={view} className="mt-6" />

          {totalPages > 1 && (
            <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <Button
                  key={i}
                  size="sm"
                  variant={currentPage === i + 1 ? "gold" : "outline"}
                  onClick={() => setPage(i + 1)}
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}

          <Label className="sr-only">End of results</Label>

          {/* Complete, always-rendered index of the fleet: one crawlable link per car,
              independent of filters, sorting and pagination. */}
          <section className="mt-14 rounded-2xl border border-border bg-card p-6 shadow-card sm:p-8">
            <h2 className="font-display text-xl font-bold">All {cars.length} cars in our fleet</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Every vehicle we rent in Bucharest, with its daily price. Prices include full
              insurance, the rovinieta, unlimited-support and free delivery in the city or at
              Otopeni Airport — with no security deposit and no credit card.
            </p>
            <ul className="mt-6 grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {[...cars]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((car) => (
                  <li key={car.slug} className="text-sm">
                    <Link
                      to="/rentals/$slug"
                      params={{ slug: car.slug }}
                      className="font-medium text-muted-foreground hover:text-gold"
                    >
                      {car.name.split(":")[0]} {car.year}
                    </Link>{" "}
                    <span className="text-xs text-muted-foreground/70">
                      €{car.pricePerDay}/day
                    </span>
                  </li>
                ))}
            </ul>
          </section>

          <section className="mt-10 grid gap-6 sm:grid-cols-2">
            {rentalFaqs.map((faq) => (
              <div key={faq.q} className="rounded-2xl border border-border bg-card p-6">
                <h2 className="font-display text-base font-bold">{faq.q}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </section>

          <p className="mt-8 text-sm text-muted-foreground">
            New to driving here? Read{" "}
            <Link to="/good-to-know" className="font-semibold text-gold hover:underline">
              good to know in Bucharest
            </Link>{" "}
            or{" "}
            <Link
              to="/car-rental-no-deposit-bucharest"
              className="font-semibold text-gold hover:underline"
            >
              how no-deposit rental works
            </Link>
            .
          </p>
        </div>
      </div>
    </div>

  );
}
